import { useState, useEffect } from "react";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckoutSetupFields } from "@/components/CheckoutSetupFields";
import type { ProductCatalogOption } from "@/types/ProductCatalog";
import { type PageOnSubmitActionType } from "@/types/pageFormSubmit";

interface FormOnSubmitEditorProps {
  formOnSubmit: Record<string, PageOnSubmitActionType[]>;
  onChange: (formOnSubmit: Record<string, PageOnSubmitActionType[]>) => void;
}

interface PageItem {
  _id: string;
  title?: string;
  route?: string;
}

interface Automation {
  _id: string;
  name?: string;
  template?: { type: string; context?: unknown } | string | boolean;
}

export function FormOnSubmitEditor({ formOnSubmit, onChange }: FormOnSubmitEditorProps) {
  const [products, setProducts] = useState<ProductCatalogOption[]>([]);
  const [pages, setPages] = useState<PageItem[]>([]);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isLoadingAutomations, setIsLoadingAutomations] = useState(false);
  const [newFormId, setNewFormId] = useState("");

  useEffect(() => {
    setIsLoadingProducts(true);
    setIsLoadingPages(true);
    setIsLoadingAutomations(true);
    Promise.all([
      fetch("/api/products").then((r) => r.json()),
      fetch("/api/pages").then((r) => r.json()),
      fetch("/api/automations").then((r) => r.json()),
    ])
      .then(([productsRes, pagesRes, automationsRes]) => {
        setProducts(productsRes.products || []);
        setPages(pagesRes.pages || []);
        setAutomations(
          (automationsRes.automations || []).filter((a: Automation) => {
            const template = a.template;
            return template !== true && typeof template !== "string";
          })
        );
      })
      .catch(console.error)
      .finally(() => {
        setIsLoadingProducts(false);
        setIsLoadingPages(false);
        setIsLoadingAutomations(false);
      });
  }, []);

  const formIds = Object.keys(formOnSubmit || {});

  const updateFormActions = (formId: string, actions: PageOnSubmitActionType[]) => {
    const next = { ...(formOnSubmit || {}) };
    if (actions.length === 0) {
      delete next[formId];
    } else {
      next[formId] = actions;
    }
    onChange(next);
  };

  const addForm = () => {
    const id = newFormId.trim();
    if (!id) return;
    if (formOnSubmit?.[id]) return;
    onChange({ ...(formOnSubmit || {}), [id]: [] });
    setNewFormId("");
  };

  const removeForm = (formId: string) => {
    const next = { ...(formOnSubmit || {}) };
    delete next[formId];
    onChange(next);
  };

  const addAction = (formId: string, type: PageOnSubmitActionType["type"]) => {
    const actions = formOnSubmit?.[formId] || [];
    const newAction: PageOnSubmitActionType =
      type === "CreateLead"
        ? { type: "CreateLead" }
        : type === "StartAutomation"
          ? { type: "StartAutomation", automationId: "" }
          : type === "GoTo"
            ? { type: "GoTo", pageId: "" }
            : type === "MailerLiteCreateSubscriber"
              ? { type: "MailerLiteCreateSubscriber" }
              : { type: "Checkout", lineItems: [] };
    updateFormActions(formId, [...actions, newAction]);
  };

  const removeAction = (formId: string, index: number) => {
    const actions = (formOnSubmit?.[formId] || []).filter((_, i) => i !== index);
    updateFormActions(formId, actions);
  };

  const updateAction = (
    formId: string,
    index: number,
    updates: Partial<PageOnSubmitActionType>
  ) => {
    const actions = [...(formOnSubmit?.[formId] || [])];
    actions[index] = { ...actions[index], ...updates } as PageOnSubmitActionType;
    updateFormActions(formId, actions);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Form submit actions</Label>
        <p className="text-xs text-gray-500">
          Map form IDs (e.g. yes-form, no-form) to actions. Forms in your HTML must have matching id attributes.
        </p>
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="Form ID (e.g. yes-form)"
          value={newFormId}
          onChange={(e) => setNewFormId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addForm()}
        />
        <Button type="button" variant="outline" onClick={addForm} disabled={!newFormId.trim()}>
          <Plus className="w-4 h-4 mr-2" />
          Add form
        </Button>
      </div>

      <div className="space-y-4">
        {formIds.map((formId) => (
          <div key={formId} className="p-4 border border-gray-200 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{formId}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeForm(formId)}
                className="h-8 w-8 text-gray-600 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-2">
              {(formOnSubmit?.[formId] || []).map((action, index) => (
                <div
                  key={index}
                  className="p-3 border border-gray-100 rounded-md space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm">
                      {action.type === "CreateLead" && "Create Lead"}
                      {action.type === "StartAutomation" && "Start Flow"}
                      {action.type === "GoTo" && "Go To"}
                      {action.type === "MailerLiteCreateSubscriber" &&
                        "MailerLite Create Subscriber"}
                      {action.type === "Checkout" && "Checkout"}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAction(formId, index)}
                      className="h-6 w-6 text-gray-600 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>

                  {action.type === "StartAutomation" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Flow</Label>
                      <Select
                        value={action.automationId || ""}
                        onValueChange={(v) =>
                          updateAction(formId, index, { automationId: v })
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={isLoadingAutomations ? "Loading..." : "Select flow"} />
                        </SelectTrigger>
                        <SelectContent>
                          {automations.map((a) => (
                            <SelectItem key={a._id} value={a._id}>
                              {a.name || a._id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {action.type === "GoTo" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Page</Label>
                      <Select
                        value={action.pageId || ""}
                        onValueChange={(v) =>
                          updateAction(formId, index, { pageId: v })
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={isLoadingPages ? "Loading..." : "Select page"} />
                        </SelectTrigger>
                        <SelectContent>
                          {pages.map((p) => (
                            <SelectItem key={p._id} value={p._id}>
                              {p.title} ({p.route})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {action.type === "Checkout" && (
                    <CheckoutSetupFields
                      idPrefix={`checkout-${formId}-${index}`}
                      value={{
                        lineItems: action.lineItems,
                        onCheckoutSuccess: action.onCheckoutSuccess,
                        checkoutSettings: action.checkoutSettings,
                      }}
                      onChange={(next) => {
                        updateAction(formId, index, {
                          lineItems: next.lineItems,
                          onCheckoutSuccess: next.onCheckoutSuccess,
                          checkoutSettings: next.checkoutSettings,
                        });
                      }}
                      products={products}
                      pages={pages}
                      automations={automations}
                      isLoadingProducts={isLoadingProducts}
                      isLoadingPages={isLoadingPages}
                      isLoadingAutomations={isLoadingAutomations}
                    />
                  )}

                  {action.type === "MailerLiteCreateSubscriber" && (
                    <div className="space-y-2">
                      <Label className="text-xs">Group IDs (optional)</Label>
                      <div className="space-y-2">
                        {(action.groupIds || []).map((gid, gi) => (
                          <div key={gi} className="flex gap-2">
                            <Input
                              value={gid}
                              onChange={(e) => {
                                const gids = [...(action.groupIds || [])];
                                gids[gi] = e.target.value.trim();
                                updateAction(formId, index, {
                                  groupIds: gids.filter(Boolean),
                                });
                              }}
                              placeholder="Group ID"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                const gids = (action.groupIds || []).filter(
                                  (_, i) => i !== gi
                                );
                                updateAction(formId, index, {
                                  groupIds: gids.length ? gids : undefined,
                                });
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const gids = [...(action.groupIds || []), ""];
                            updateAction(formId, index, { groupIds: gids });
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add group
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <Select
                value=""
                onValueChange={(v) => addAction(formId, v as PageOnSubmitActionType["type"])}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Add action..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CreateLead">Create Lead</SelectItem>
                  <SelectItem value="StartAutomation">Start Flow</SelectItem>
                  <SelectItem value="GoTo">Go To</SelectItem>
                  <SelectItem value="MailerLiteCreateSubscriber">MailerLite Create Subscriber</SelectItem>
                  <SelectItem value="Checkout">Checkout</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
